import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAllUserSubscriptions } from '@/hooks/useAllUserSubscriptions';
import { useEntitlement } from '@/hooks/useEntitlement';

interface ExamContextType {
  selectedExamSlug: string | null; // null means "All Exams"
  setSelectedExamSlug: (slug: string | null) => void;
  availableExams: Array<{
    slug: string;
    name: string;
    icon?: string;
    color?: string;
  }>;
  isLoading: boolean;
  isAutoSelecting: boolean;
}

const ExamContext = createContext<ExamContextType | undefined>(undefined);

export const useExamContext = () => {
  const context = useContext(ExamContext);
  if (context === undefined) {
    throw new Error('useExamContext must be used within an ExamProvider');
  }
  return context;
};

interface ExamProviderProps {
  children: ReactNode;
}

const sb = supabase as any;

export const ExamProvider = ({ children }: ExamProviderProps) => {
  const [selectedExamSlug, setSelectedExamSlug] = useState<string | null>(null);
  const [isAutoSelecting, setIsAutoSelecting] = useState(true);
  const { subscriptions, loading } = useAllUserSubscriptions();
  // Real admins (even when "View as User" is on) get access to ALL exams so they can preview content.
  const { isRealAdmin } = useEntitlement();

  // For admins, fetch all active exam labels so they can switch between any exam.
  const { data: allExams = [], isLoading: examsLoading } = useQuery({
    queryKey: ['all-exam-labels-admin'],
    enabled: isRealAdmin,
    queryFn: async () => {
      const { data, error } = await sb
        .from('exam_labels')
        .select('slug, name, icon, color')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const subscriptionExams = subscriptions
    .filter((sub) => !sub.isExpired)
    .map((sub) => ({
      slug: sub.examSlug,
      name: sub.examName,
      icon: sub.examIcon,
      color: sub.examColor,
    }));

  // Admins see ALL exams; regular users see only their subscribed exams.
  const availableExams = isRealAdmin
    ? allExams.map((e: any) => ({ slug: e.slug, name: e.name, icon: e.icon, color: e.color }))
    : subscriptionExams;

  const isLoadingCombined = loading || (isRealAdmin && examsLoading);

  // Auto-select if only one exam available
  useEffect(() => {
    if (!isLoadingCombined) {
      if (availableExams.length === 1 && selectedExamSlug === null) {
        setSelectedExamSlug(availableExams[0].slug);
      }
      setIsAutoSelecting(false);
    }
  }, [availableExams.length, isLoadingCombined, selectedExamSlug]);

  const value = {
    selectedExamSlug,
    setSelectedExamSlug,
    availableExams,
    isLoading: isLoadingCombined,
    isAutoSelecting,
  };

  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
};
